export type Channel = "web" | "whatsapp" | "instagram"

export type PlanKey = "free" | "starter" | "business" | "growth" | "scale" | "enterprise"

export type Plan = {
  key: PlanKey
  monthlyPrice: number | null
  yearlyPrice: number | null
  features: {
    credits: number | "custom"
    topUpPricePerHundred: number
    channels: Channel[]
    outbound: boolean
    capabilities: number | "unlimited"
    websiteEntries: number | boolean
    mcp: boolean
    controlHistory: boolean
    multiAccount: boolean
  }
  isPopular?: boolean
  isHidden?: boolean
  isContactUs?: boolean
}

export const PLANS: readonly Plan[] = [
  {
    key: "free",
    monthlyPrice: 0,
    yearlyPrice: 0,
    features: {
      credits: 50,
      topUpPricePerHundred: 45,
      channels: ["web"],
      outbound: false,
      capabilities: 3,
      websiteEntries: false,
      mcp: false,
      controlHistory: false,
      multiAccount: false,
    },
  },
  {
    key: "starter",
    monthlyPrice: 140,
    yearlyPrice: 110,
    features: {
      credits: 300,
      topUpPricePerHundred: 40,
      channels: ["web", "whatsapp", "instagram"],
      outbound: true,
      capabilities: 3,
      websiteEntries: 3,
      mcp: false,
      controlHistory: false,
      multiAccount: false,
    },
  },
  {
    key: "business",
    monthlyPrice: 390,
    yearlyPrice: 310,
    features: {
      credits: 1000,
      topUpPricePerHundred: 35,
      channels: ["web", "whatsapp", "instagram"],
      outbound: true,
      capabilities: 5,
      websiteEntries: 10,
      mcp: false,
      controlHistory: false,
      multiAccount: false,
    },
    isPopular: true,
  },
  {
    key: "growth",
    monthlyPrice: 790,
    yearlyPrice: 630,
    features: {
      credits: 3000,
      topUpPricePerHundred: 30,
      channels: ["web", "whatsapp", "instagram"],
      outbound: true,
      capabilities: 10,
      websiteEntries: true,
      mcp: true,
      controlHistory: false,
      multiAccount: false,
    },
    isHidden: true,
  },
  {
    key: "scale",
    monthlyPrice: 1590,
    yearlyPrice: 1270,
    features: {
      credits: 5000,
      topUpPricePerHundred: 25,
      channels: ["web", "whatsapp", "instagram"],
      outbound: true,
      capabilities: "unlimited",
      websiteEntries: true,
      mcp: true,
      controlHistory: true,
      multiAccount: true,
    },
  },
  {
    key: "enterprise",
    monthlyPrice: null,
    yearlyPrice: null,
    features: {
      credits: "custom",
      topUpPricePerHundred: 20,
      channels: ["web", "whatsapp", "instagram"],
      outbound: true,
      capabilities: "unlimited",
      websiteEntries: true,
      mcp: true,
      controlHistory: true,
      multiAccount: true,
    },
    isContactUs: true,
  },
]

export type PlanDefaultTranslations = {
  plans: Record<PlanKey, { name: string; description: string }>
  features: Record<string, (plan: Plan) => string>
}

export const PLAN_DEFAULT_TRANSLATIONS: PlanDefaultTranslations = {
  plans: {
    free: { name: "Free", description: "Great for testing Permut." },
    starter: { name: "Starter", description: "For small size businesses." },
    business: { name: "Business", description: "For medium size businesses." },
    growth: { name: "Growth", description: "For growing businesses." },
    scale: { name: "Scale", description: "For large size businesses." },
    enterprise: { name: "Enterprise", description: "For enterprises and beyond." },
  },
  features: {
    credits: (p) =>
      p.features.credits === "custom"
        ? "Custom monthly credits"
        : `${(p.features.credits as number).toLocaleString("de-DE")} monthly credits`,
    topUpPricePerHundred: (p) => `100 credit top-up: ${p.features.topUpPricePerHundred}€`,
    channels: (p) => (p.features.channels.length > 1 ? "All channels" : "Web Widget only"),
    outbound: (p) => (p.features.outbound ? "Outbound messaging" : ""),
    capabilities: (p) =>
      p.features.capabilities === "unlimited"
        ? "Unlimited capabilities"
        : `${p.features.capabilities} capabilities`,
    websiteEntries: (p) => {
      if (!p.features.websiteEntries) return ""
      if (p.features.websiteEntries === true) return "Unlimited website entries"
      return `${p.features.websiteEntries} website entries`
    },
    mcp: (p) => (p.features.mcp ? "MCP integration" : ""),
    controlHistory: (p) => (p.features.controlHistory ? "Control history" : ""),
    multiAccount: (p) => (p.features.multiAccount ? "Multi-account" : ""),
  },
}
